import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
  AlertCircle,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const HACCP_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <AlertCircle size={18} className="text-red-400" />
            <span>Food Safety Management</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-red-400 via-orange-400 to-amber-400">
            HACCP Certification
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Hazard Analysis and Critical Control Points - Systematic Approach to
            Food Safety Management and Risk Control
          </p>
        </motion.div>
      </section>

      {/* What is HACCP */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is HACCP Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              HACCP (Hazard Analysis and Critical Control Points) is an
              internationally recognized and systematic approach to food safety
              management. It focuses on identifying hazards, assessing the risks
              they pose, and implementing critical control measures at various
              stages of food production, processing, and handling to ensure that
              food is safe for consumption.
            </p>
            <p>
              HACCP is a preventive system designed to manage food safety risks,
              rather than reacting to food safety issues once they occur. HACCP
              certification confirms that an organization has implemented an
              effective food safety management system based on the HACCP
              principles, ensuring the organization consistently identifies,
              evaluates, and controls food safety hazards throughout the food
              production and handling process.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of HACCP Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Food Safety Assurance",
                desc: "Identifies food safety hazards and implements controls to ensure food is safe for consumption.",
                icon: ShieldCheck,
              },
              {
                title: "Risk-Based Approach",
                desc: "Analyzes and manages biological, chemical, and physical risks in food production.",
                icon: Target,
              },
              {
                title: "Critical Control Points",
                desc: "Identifies key stages in the production process where risks must be controlled or eliminated.",
                icon: AlertCircle,
              },
              {
                title: "Regulatory Compliance",
                desc: "Ensures adherence to food safety laws, regulations, and industry standards.",
                icon: CheckCircle,
              },
              {
                title: "Documentation & Records",
                desc: "Maintains a clear record of all processes, monitoring results, and corrective actions.",
                icon: FileCheck,
              },
              {
                title: "Preventive Focus",
                desc: "Proactively prevents food safety issues rather than reacting to contamination.",
                icon: Award,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-red-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* HACCP System */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is a HACCP System?
          </h2>
          <div className="bg-gradient-to-r from-red-50 to-orange-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              A HACCP system is a proactive approach to food safety that
              includes seven key principles:
            </p>
            <ul className="space-y-3">
              {[
                "Hazard Identification - Identifying potential biological, chemical, and physical hazards in food production",
                "Critical Control Points (CCPs) - Identifying key stages where hazards can be controlled",
                "Critical Limits - Defining measurable criteria (temperature, time, pH levels) for each CCP",
                "Monitoring - Regularly checking CCPs to ensure they are under control",
                "Corrective Actions - Taking action when a CCP exceeds its critical limit",
                "Verification - Verifying that the HACCP plan is working effectively",
                "Record Keeping - Maintaining accurate records of all food safety procedures and actions",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Importance of HACCP Certification for Businesses
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Provides a systematic approach to identifying and controlling food safety risks",
              "Ensures compliance with FDA, EFSA, and Codex Alimentarius guidelines",
              "Increases customer trust by demonstrating commitment to food safety",
              "Facilitates market access to countries requiring HACCP certification",
              "Reduces food safety risks, recalls, and operational liabilities",
              "Improves overall operational efficiency and product quality consistency",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Significance of HACCP Certification
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            HACCP certification helps organizations:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "Demonstrate commitment to food safety and consumer protection",
              "Meet regulatory requirements and industry standards",
              "Establish and maintain food safety controls across production",
              "Improve quality control and reduce foodborne illness risks",
              "Gain recognition and market access for exports",
              "Build consumer confidence through proven safety systems",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-red-100 to-orange-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-red-600 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of HACCP Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Reduces risk of contamination, spoilage, and foodborne illness",
              "Helps meet food safety regulatory requirements across regions",
              "Systematically identifies and controls food safety risks",
              "Increases consumer confidence in product safety and quality",
              "Reduces food waste, product recalls, and associated costs",
              "Increases reliability of food safety processes",
              "Improves organizational food safety culture",
              "Strengthens supply chain credibility and traceability",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries That Require HACCP Certification
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            HACCP certification is essential for all sectors of the food
            industry and food-related services:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Food Manufacturers",
              "Food Processors",
              "Packaged Foods",
              "Beverage Production",
              "Food Distribution & Logistics",
              "Retailers and Restaurants",
              "Meat, Poultry, and Seafood Processing",
              "Dairy and Dairy Products",
              "Catering Services",
              "Food Service Providers",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg"
              >
                <Factory className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Certification */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Get HACCP Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Hazard Analysis",
                desc: "Identify and assess all potential food safety hazards in your process",
              },
              {
                step: 2,
                title: "Develop HACCP Plan",
                desc: "Outline procedures for controlling hazards and identifying CCPs",
              },
              {
                step: 3,
                title: "Employee Training",
                desc: "Ensure staff are trained on food safety and HACCP principles",
              },
              {
                step: 4,
                title: "Implementation",
                desc: "Put in place control measures, monitoring systems, and corrective actions",
              },
              {
                step: 5,
                title: "Internal Audits",
                desc: "Regularly audit processes to verify HACCP effectiveness",
              },
              {
                step: 6,
                title: "Certification Audit",
                desc: "Undergo third-party audit by an accredited certification body",
              },
              {
                step: 7,
                title: "Continuous Improvement",
                desc: "Continuously monitor and improve the HACCP system",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-red-50 via-orange-50 to-amber-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for HACCP Certification?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions Pvt. Ltd. provides expert HACCP consultancy
              services, helping food businesses design, implement, and maintain
              a robust food safety management system that ensures compliance
              with international food safety standards.
            </p>
            <p className="text-lg text-gray-700 mb-6">Our services include:</p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Hazard analysis and risk assessment",
                "HACCP plan development and implementation",
                "Employee training and awareness programs",
                "Internal audits and pre-certification support",
                "Certification body coordination and audit support",
                "Ongoing system improvement and validation",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help food businesses achieve safe production, regulatory
              compliance, and a strong reputation through practical HACCP
              implementation.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-red-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-red-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default HACCP_Details;
